import { useState, useEffect, useMemo, useCallback } from 'react';
import { adminAPI, documentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Loader2, Plus, Search, CheckCircle, XCircle, Clock, Eye, Download, AlertTriangle, FolderOpen, Edit, Trash2, MoreHorizontal, Save, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell, SortIcon, createSortHelpers } from '../components/AdminShared';

const LN = { en:'English', es:'Spanish', zh:'Mandarin', ar:'Arabic', vi:'Vietnamese', fr:'French', tl:'Tagalog', ko:'Korean', ru:'Russian', pt:'Portuguese', hi:'Hindi', de:'German', ja:'Japanese', it:'Italian' };
const LC = { en:'EN', es:'ES', zh:'ZH', ar:'AR', vi:'VI', fr:'FR', tl:'TL', ko:'KO', ru:'RU', pt:'PT', hi:'HI', de:'DE', ja:'JA', it:'IT' };

function LangPairBadge({ pair }) {
  const [from, to] = pair.split('-');
  return (
    <TooltipProvider><Tooltip delayDuration={200}><TooltipTrigger asChild>
      <Badge variant="outline" className="text-[10px] font-mono cursor-default">{(LC[from]||from?.toUpperCase())}-{(LC[to]||to?.toUpperCase())}</Badge>
    </TooltipTrigger><TooltipContent><p className="text-xs">{LN[from]||from} &rarr; {LN[to]||to}</p></TooltipContent></Tooltip></TooltipProvider>
  );
}
function LangBadge({ code }) {
  return (
    <TooltipProvider><Tooltip delayDuration={200}><TooltipTrigger asChild>
      <Badge variant="outline" className="text-[10px] font-mono cursor-default">{LC[code]||code?.toUpperCase()}</Badge>
    </TooltipTrigger><TooltipContent><p className="text-xs">{LN[code]||code}</p></TooltipContent></Tooltip></TooltipProvider>
  );
}

export default function AdminInterpretersPage() {
  const [interpreters, setInterpreters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [langPairFilter, setLangPairFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [languages, setLanguages] = useState([]);
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Create interpreter
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', name: '', phone: '', languages: [], language_pairs: [], scope: 'global', credential_tags: '', interpreter_group_id: '' });
  const [creating, setCreating] = useState(false);

  // Detail / Edit
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Group management
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });

  const loadAll = useCallback(async () => {
    try {
      const [iRes, lRes, gRes] = await Promise.all([
        adminAPI.listInterpreters({}),
        adminAPI.listLanguages(),
        adminAPI.listInterpreterGroups(),
      ]);
      setInterpreters(iRes.data);
      setLanguages(lRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setGroups(gRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadInterpreters = useCallback(async () => {
    try {
      const res = await adminAPI.listInterpreters({ status: statusFilter });
      setInterpreters(res.data);
    } catch (e) { console.error(e); }
  }, [statusFilter]);

  useEffect(() => { if (!loading) loadInterpreters(); }, [loading, loadInterpreters]);

  const { toggleSort } = createSortHelpers(sortCol, sortDir, setSortCol, setSortDir);

  const allLangPairs = useMemo(() => {
    const pairs = new Set();
    interpreters.forEach(i => (i.language_pairs || []).forEach(p => pairs.add(p)));
    return [...pairs].sort();
  }, [interpreters]);

  const filtered = useMemo(() => {
    let list = interpreters.filter(i => {
      const matchSearch = !search || i.name?.toLowerCase().includes(search.toLowerCase()) || i.email?.toLowerCase().includes(search.toLowerCase());
      const matchLang = !langPairFilter || (i.language_pairs || []).includes(langPairFilter);
      const matchGroup = !groupFilter || (groupFilter === '_none' ? !i.interpreter_group_id : i.interpreter_group_id === groupFilter);
      return matchSearch && matchLang && matchGroup;
    });
    list = [...list].sort((a, b) => {
      let va = '', vb = '';
      if (sortCol === 'name') { va = a.name || ''; vb = b.name || ''; }
      else if (sortCol === 'email') { va = a.email || ''; vb = b.email || ''; }
      else if (sortCol === 'status') { va = a.approval_status || ''; vb = b.approval_status || ''; }
      else if (sortCol === 'scope') { va = a.scope || ''; vb = b.scope || ''; }
      else if (sortCol === 'group') { va = a.interpreter_group_name || ''; vb = b.interpreter_group_name || ''; }
      else if (sortCol === 'langs') { va = (a.language_pairs || a.languages || []).join(','); vb = (b.language_pairs || b.languages || []).join(','); }
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [interpreters, search, langPairFilter, groupFilter, sortCol, sortDir]);

  // ─── Create Interpreter ───
  const toggleLangPair = (pair) => {
    setCreateForm(p => ({ ...p, language_pairs: p.language_pairs.includes(pair) ? p.language_pairs.filter(x => x !== pair) : [...p.language_pairs, pair] }));
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const tags = createForm.credential_tags.split(',').map(t => t.trim()).filter(Boolean);
      const uniqueLangs = [...new Set(createForm.language_pairs.flatMap(p => p.split('-')))];
      await adminAPI.createInterpreter({ ...createForm, credential_tags: tags, languages: uniqueLangs });
      setShowCreate(false);
      setCreateForm({ email: '', password: '', name: '', phone: '', languages: [], language_pairs: [], scope: 'global', credential_tags: '', interpreter_group_id: '' });
      toast.success('Interpreter created');
      loadInterpreters();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  // ─── Detail / Edit ───
  const openDetail = async (interp) => {
    setShowDetail(interp);
    setEditing(false);
    setDetailLoading(true);
    try {
      const res = await adminAPI.getInterpreter(interp.user_id || interp.id);
      setDetailData(res.data);
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  const startEditing = () => {
    const p = detailData?.profile || showDetail;
    setEditForm({
      name: showDetail.name || '',
      phone: p?.phone || showDetail.phone || '',
      scope: p?.scope || 'global',
      interpreter_group_id: p?.interpreter_group_id || '',
      credential_tags: (p?.credential_tags || []).join(', '),
      address_line1: p?.address_line1 || '',
      address_line2: p?.address_line2 || '',
      city: p?.city || '',
      state: p?.state || '',
      zip_code: p?.zip_code || '',
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const tags = editForm.credential_tags.split(',').map(t => t.trim()).filter(Boolean);
      await adminAPI.updateInterpreter(showDetail.user_id || showDetail.id, {
        name: editForm.name,
        phone: editForm.phone,
        scope: editForm.scope,
        interpreter_group_id: editForm.interpreter_group_id,
        credential_tags: tags,
        address_line1: editForm.address_line1,
        address_line2: editForm.address_line2,
        city: editForm.city,
        state: editForm.state,
        zip_code: editForm.zip_code,
      });
      toast.success('Profile updated');
      setEditing(false);
      loadInterpreters();
      // Refresh detail
      const res = await adminAPI.getInterpreter(showDetail.user_id || showDetail.id);
      setDetailData(res.data);
      setShowDetail(prev => ({ ...prev, name: editForm.name, phone: editForm.phone, scope: editForm.scope, interpreter_group_id: editForm.interpreter_group_id }));
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  // ─── Approve / Reject ───
  const handleApprove = async (id) => {
    try { await adminAPI.approveInterpreter(id); toast.success('Approved'); loadInterpreters(); setShowDetail(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleReject = async (id) => {
    try { await adminAPI.rejectInterpreter(id); toast.success('Rejected'); loadInterpreters(); setShowDetail(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ─── Document actions ───
  const handleDocAction = async (docId, action) => {
    try {
      if (action === 'approve') await documentsAPI.approve(docId);
      else await documentsAPI.reject(docId);
      toast.success(`Document ${action}d`);
      if (showDetail) { const res = await adminAPI.getInterpreter(showDetail.user_id || showDetail.id); setDetailData(res.data); }
    } catch { toast.error(`Failed to ${action} document`); }
  };

  // ─── Group Management ───
  const openCreateGroup = () => { setEditGroup(null); setGroupForm({ name: '', description: '' }); setShowGroupDialog(true); };
  const openEditGroup = (g) => { setEditGroup(g); setGroupForm({ name: g.name, description: g.description || '' }); setShowGroupDialog(true); };
  const handleGroupSubmit = async () => {
    try {
      if (editGroup) await adminAPI.updateInterpreterGroup(editGroup.id, groupForm);
      else await adminAPI.createInterpreterGroup(groupForm);
      setShowGroupDialog(false);
      toast.success(editGroup ? 'Group updated' : 'Group created');
      const gRes = await adminAPI.listInterpreterGroups();
      setGroups(gRes.data);
    } catch (e) { toast.error('Failed'); }
  };
  const handleDeleteGroup = async (gid) => {
    try {
      await adminAPI.deleteInterpreterGroup(gid);
      toast.success('Group deleted');
      const gRes = await adminAPI.listInterpreterGroups();
      setGroups(gRes.data);
      loadInterpreters();
    } catch { toast.error('Failed'); }
  };

  const statusIcon = (s) => s === 'approved' ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : s === 'rejected' ? <XCircle className="w-3.5 h-3.5 text-red-500" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />;
  const groupMap = useMemo(() => Object.fromEntries(groups.map(g => [g.id, g.name])), [groups]);

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell
      title="Interpreters"
      subtitle="Manage interpreter profiles, groups, and approvals"
      testId="admin-interpreters-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openCreateGroup} data-testid="manage-groups-button"><FolderOpen className="w-4 h-4 mr-1" />Add Group</Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-interpreter-button"><Plus className="w-4 h-4 mr-2" />Add Interpreter</Button>
        </div>
      }
    >
      {/* Groups summary bar */}
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {groups.map(g => (
            <div key={g.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-card text-sm">
              <span className="font-medium">{g.name}</span>
              <Badge variant="secondary" className="text-[10px]">{g.member_count || 0}</Badge>
              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => openEditGroup(g)} data-testid={`edit-group-${g.id}`}><Edit className="w-3 h-3" /></Button>
              <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={() => handleDeleteGroup(g.id)} data-testid={`delete-group-${g.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 flex-1 max-w-sm"><Search className="w-4 h-4 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} data-testid="interpreter-search" /></div>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Statuses</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={v => setGroupFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="group-filter"><SelectValue placeholder="All Groups" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Groups</SelectItem>
            <SelectItem value="_none">Ungrouped</SelectItem>
            {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={langPairFilter} onValueChange={v => setLangPairFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="lang-pair-filter"><SelectValue placeholder="All Language Pairs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Language Pairs</SelectItem>
            {allLangPairs.map(p => { const [from, to] = p.split('-'); return <SelectItem key={p} value={p}>{(LC[from]||from?.toUpperCase())} &rarr; {(LC[to]||to?.toUpperCase())} ({LN[to]||to})</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card"><ScrollArea className="max-h-[600px]"><Table><TableHeader><TableRow>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-interp-name">Name<SortIcon col="name" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('email')}>Email<SortIcon col="email" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('group')}>Group<SortIcon col="group" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('langs')}>Language Pairs<SortIcon col="langs" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('scope')}>Scope<SortIcon col="scope" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead>Actions</TableHead>
      </TableRow></TableHeader><TableBody>
        {filtered.map(i => (
          <TableRow key={i.user_id || i.id} data-testid={`interp-row-${i.user_id}`}>
            <TableCell className="font-medium">{i.name}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{i.email}</TableCell>
            <TableCell>
              {i.interpreter_group_name ? <Badge variant="outline" className="text-xs">{i.interpreter_group_name}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
            </TableCell>
            <TableCell><div className="flex flex-wrap gap-1">
              {(i.language_pairs || []).map(p => <LangPairBadge key={p} pair={p} />)}
              {(!i.language_pairs || i.language_pairs.length === 0) && (i.languages || []).map(l => <LangBadge key={l} code={l} />)}
            </div></TableCell>
            <TableCell><Badge variant="secondary" className="text-xs capitalize">{i.scope}</Badge></TableCell>
            <TableCell><div className="flex items-center gap-1.5">{statusIcon(i.approval_status)}<span className="text-xs capitalize">{i.approval_status}</span></div></TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openDetail(i)} data-testid={`view-interp-${i.user_id}`}><Eye className="w-4 h-4" /></Button>
                {i.approval_status === 'pending' && <>
                  <Button variant="ghost" size="icon" onClick={() => handleApprove(i.user_id)} className="text-green-600" data-testid={`approve-interp-${i.user_id}`}><CheckCircle className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleReject(i.user_id)} className="text-destructive"><XCircle className="w-4 h-4" /></Button>
                </>}
              </div>
            </TableCell>
          </TableRow>
        ))}
        {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No interpreters found</TableCell></TableRow>}
      </TableBody></Table></ScrollArea></div>

      {/* ═══ Create Interpreter Dialog ═══ */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-interpreter-dialog">
          <DialogHeader><DialogTitle>Add Interpreter</DialogTitle><DialogDescription>Create interpreter with language pairs and group assignment</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name *</Label><Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} data-testid="interp-name" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} data-testid="interp-email" /></div>
              <div className="space-y-2"><Label>Password *</Label><Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} data-testid="interp-password" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={createForm.phone} onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Scope</Label>
                <Select value={createForm.scope} onValueChange={v => setCreateForm(p => ({ ...p, scope: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="global">Global</SelectItem><SelectItem value="domestic">Domestic</SelectItem><SelectItem value="international">International</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Group</Label>
                <Select value={createForm.interpreter_group_id || '_none'} onValueChange={v => setCreateForm(p => ({ ...p, interpreter_group_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger data-testid="interp-group-select"><SelectValue placeholder="No group" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">No group</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2">
              <Label>Language Pairs (base &rarr; target)</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto border rounded-md p-2">
                {languages.map(lang => {
                  if (lang.code === 'en') return null;
                  const pair = `en-${lang.code}`;
                  return (
                    <label key={pair} className="flex items-center gap-1.5 cursor-pointer text-sm hover:bg-accent rounded px-1 py-0.5">
                      <Checkbox checked={createForm.language_pairs.includes(pair)} onCheckedChange={() => toggleLangPair(pair)} data-testid={`pair-${pair}`} />
                      <span className="font-mono text-xs">{LC.en||'EN'}-{LC[lang.code]||lang.code.toUpperCase()}</span>
                      <span className="text-xs text-muted-foreground truncate">({lang.name})</span>
                    </label>
                  );
                })}
              </div>
              {createForm.language_pairs.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{createForm.language_pairs.map(p => <LangPairBadge key={p} pair={p} />)}</div>}
            </div>
            <div className="space-y-2"><Label>Credential Tags (comma-separated)</Label><Input value={createForm.credential_tags} onChange={e => setCreateForm(p => ({ ...p, credential_tags: e.target.value }))} placeholder="ATA, AIIC" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!createForm.name || !createForm.email || !createForm.password || creating}
              className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-create-interpreter">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Detail/Edit Dialog ═══ */}
      <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailData(null); setEditing(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="interpreter-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showDetail?.name}
              {!editing && <Button variant="ghost" size="sm" onClick={startEditing} data-testid="edit-profile-button"><Edit className="w-4 h-4 mr-1" />Edit</Button>}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : showDetail && (
            <Tabs defaultValue="profile" className="mt-2">
              <TabsList className="grid w-full grid-cols-3 h-9">
                <TabsTrigger value="profile" className="text-xs">Profile</TabsTrigger>
                <TabsTrigger value="documents" className="text-xs">Documents {detailData?.documents?.length > 0 && `(${detailData.documents.length})`}</TabsTrigger>
                <TabsTrigger value="lang-requests" className="text-xs">Lang Requests {detailData?.language_requests?.length > 0 && `(${detailData.language_requests.length})`}</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                {editing ? (
                  <div className="space-y-4 py-2" data-testid="edit-profile-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-interp-name" /></div>
                      <div className="space-y-2"><Label>Phone</Label>
                        <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input className="pl-10" value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-interp-phone" /></div></div>
                      <div className="space-y-2"><Label>Scope</Label>
                        <Select value={editForm.scope} onValueChange={v => setEditForm(p => ({ ...p, scope: v }))}><SelectTrigger data-testid="edit-interp-scope"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="global">Global</SelectItem><SelectItem value="domestic">Domestic</SelectItem><SelectItem value="international">International</SelectItem></SelectContent></Select></div>
                      <div className="space-y-2"><Label>Group</Label>
                        <Select value={editForm.interpreter_group_id || '_none'} onValueChange={v => setEditForm(p => ({ ...p, interpreter_group_id: v === '_none' ? '' : v }))}>
                          <SelectTrigger data-testid="edit-interp-group"><SelectValue placeholder="No group" /></SelectTrigger>
                          <SelectContent><SelectItem value="_none">No group</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div>
                    </div>
                    <div className="space-y-2"><Label>Credential Tags</Label><Input value={editForm.credential_tags} onChange={e => setEditForm(p => ({ ...p, credential_tags: e.target.value }))} placeholder="ATA, AIIC" data-testid="edit-interp-tags" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Address Line 1</Label><Input value={editForm.address_line1} onChange={e => setEditForm(p => ({ ...p, address_line1: e.target.value }))} /></div>
                      <div className="space-y-2"><Label>Address Line 2</Label><Input value={editForm.address_line2} onChange={e => setEditForm(p => ({ ...p, address_line2: e.target.value }))} /></div>
                      <div className="space-y-2"><Label>City</Label><Input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2"><Label>State</Label><Input value={editForm.state} onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))} /></div>
                        <div className="space-y-2"><Label>ZIP</Label><Input value={editForm.zip_code} onChange={e => setEditForm(p => ({ ...p, zip_code: e.target.value }))} /></div>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                      <Button onClick={handleSaveEdit} disabled={saving} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-profile-button">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div><span className="text-muted-foreground">Email:</span> {showDetail.email}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {showDetail.phone || '-'}</div>
                      <div><span className="text-muted-foreground">Scope:</span> <Badge variant="secondary" className="text-xs capitalize">{showDetail.scope}</Badge></div>
                      <div><span className="text-muted-foreground">Status:</span> <span className="capitalize">{showDetail.approval_status}</span></div>
                      <div><span className="text-muted-foreground">Group:</span> {groupMap[showDetail.interpreter_group_id] ? <Badge variant="outline" className="text-xs">{groupMap[showDetail.interpreter_group_id]}</Badge> : <span>-</span>}</div>
                    </div>
                    <div><span className="text-sm text-muted-foreground">Language Pairs:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(showDetail.language_pairs || []).map(p => <LangPairBadge key={p} pair={p} />)}
                        {(!showDetail.language_pairs || showDetail.language_pairs.length === 0) && (showDetail.languages || []).map(l => <LangBadge key={l} code={l} />)}
                      </div>
                    </div>
                    {showDetail.credential_tags?.length > 0 && <div><span className="text-sm text-muted-foreground">Credentials:</span>
                      <div className="flex flex-wrap gap-1 mt-1">{showDetail.credential_tags.map(t => <Badge key={t} className="text-xs">{t}</Badge>)}</div></div>}
                    <div><span className="text-sm text-muted-foreground">Address:</span>
                      <p className="text-sm">{[showDetail.address_line1, showDetail.address_line2, showDetail.city, showDetail.state, showDetail.zip_code].filter(Boolean).join(', ') || '-'}</p></div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="documents">
                {!detailData?.documents || detailData.documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No documents uploaded</p>
                ) : (
                  <div className="space-y-2 py-2">
                    {detailData.documents.some(d => d.status === 'pending') && (
                      <div className="flex items-center gap-2 p-2 rounded-md bg-amber-500/10 text-amber-700 text-xs"><AlertTriangle className="w-4 h-4" />Documents pending review</div>
                    )}
                    <Table><TableHeader><TableRow><TableHead>File</TableHead><TableHead>Type</TableHead><TableHead>Uploaded</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                      <TableBody>{detailData.documents.map(doc => (
                        <TableRow key={doc.id}>
                          <TableCell className="text-sm">{doc.filename}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px] capitalize">{(doc.document_type||'general').replace(/_/g,' ')}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{new Date(doc.uploaded_at).toLocaleDateString()}</TableCell>
                          <TableCell><Badge variant={doc.status==='approved'?'secondary':doc.status==='rejected'?'destructive':'default'} className="text-xs capitalize">{doc.status}</Badge></TableCell>
                          <TableCell><div className="flex gap-1">
                            <a href={documentsAPI.downloadUrl(doc.id)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-7 w-7"><Download className="w-3.5 h-3.5" /></Button></a>
                            {doc.status==='pending'&&<><Button variant="ghost" size="icon" className="text-green-600 h-7 w-7" onClick={()=>handleDocAction(doc.id,'approve')}><CheckCircle className="w-3.5 h-3.5"/></Button>
                            <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={()=>handleDocAction(doc.id,'reject')}><XCircle className="w-3.5 h-3.5"/></Button></>}
                          </div></TableCell>
                        </TableRow>
                      ))}</TableBody></Table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="lang-requests">
                {!detailData?.language_requests || detailData.language_requests.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No language requests</p>
                ) : (
                  <Table><TableHeader><TableRow><TableHead>Language</TableHead><TableHead>Notes</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                    <TableBody>{detailData.language_requests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell className="text-sm">{req.language_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{req.notes||'-'}</TableCell>
                        <TableCell><Badge variant={req.status==='approved'?'secondary':req.status==='rejected'?'destructive':'default'} className="text-xs capitalize">{req.status}</Badge></TableCell>
                        <TableCell>{req.status==='pending'&&<div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="text-green-600 h-7 w-7" onClick={async()=>{await adminAPI.approveLanguageRequest(req.id);toast.success('Approved');const r=await adminAPI.getInterpreter(showDetail.user_id||showDetail.id);setDetailData(r.data);}}><CheckCircle className="w-3.5 h-3.5"/></Button>
                          <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={async()=>{await adminAPI.rejectLanguageRequest(req.id);toast.success('Rejected');const r=await adminAPI.getInterpreter(showDetail.user_id||showDetail.id);setDetailData(r.data);}}><XCircle className="w-3.5 h-3.5"/></Button>
                        </div>}</TableCell>
                      </TableRow>
                    ))}</TableBody></Table>
                )}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            {showDetail?.approval_status === 'pending' && (
              <div className="flex gap-2 mr-auto">
                <Button variant="outline" className="text-destructive" onClick={() => handleReject(showDetail.user_id||showDetail.id)}>Reject</Button>
                <Button className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" onClick={() => handleApprove(showDetail.user_id||showDetail.id)}>Approve</Button>
              </div>
            )}
            <Button variant="outline" onClick={() => { setShowDetail(null); setDetailData(null); setEditing(false); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Group Add/Edit Dialog ═══ */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent data-testid="interpreter-group-dialog">
          <DialogHeader><DialogTitle>{editGroup ? 'Edit Group' : 'Add Interpreter Group'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Group Name *</Label><Input value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))} placeholder="Medical Specialists" data-testid="group-name-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={groupForm.description} onChange={e => setGroupForm(p => ({ ...p, description: e.target.value }))} placeholder="Interpreters specializing in medical terminology" data-testid="group-desc-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={handleGroupSubmit} disabled={!groupForm.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-group">{editGroup ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
